import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  MAX_RECORDING_MS, describeVoiceError, hasRecordedAudio, pickMimeType, voiceReducer, type VoiceState,
} from '@/lib/voiceInputLogic';

interface Options {
  onText: (text: string) => void;
  onWarning?: (message: string) => void;
  onError?: (message: string) => void;
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(blob);
  });
}

/** Records speech and turns it into text via the transcribe-audio function. Never sends the chat message. */
export function useVoiceInput({ onText, onWarning, onError }: Options) {
  const [state, dispatch] = useReducer(voiceReducer, 'idle' as VoiceState);
  const [available, setAvailable] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startingRef = useRef(false);
  const unmountedRef = useRef(false);
  const [starting, setStarting] = useState(false);
  const cbRef = useRef({ onText, onWarning, onError });
  cbRef.current = { onText, onWarning, onError };

  // Show the mic only when the browser can record and an admin has configured transcription.
  useEffect(() => {
    let cancelled = false;
    const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
      && typeof MediaRecorder !== 'undefined' && pickMimeType((t) => MediaRecorder.isTypeSupported(t)) !== null;
    if (!supported) return;
    supabase.functions.invoke('transcribe-audio', { body: { check: true } })
      .then(({ data, error }) => { if (!cancelled && !error && data?.configured === true) setAvailable(true); })
      .catch(() => { /* leave the mic hidden */ });
    return () => { cancelled = true; };
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current !== null) { window.clearInterval(timerRef.current); timerRef.current = null; }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // On unmount: detach onstop so a stopped recorder never triggers an upload, then release everything.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      const rec = recorderRef.current;
      if (rec) {
        rec.onstop = null;
        rec.onerror = null;
        rec.ondataavailable = null;
        try { if (rec.state !== 'inactive') rec.stop(); } catch { /* already stopped */ }
      }
      chunksRef.current = [];
      cleanup();
    };
  }, [cleanup]);

  const transcribe = useCallback(async (blob: Blob, mimeType: string) => {
    try {
      const audio_base64 = await blobToBase64(blob);
      if (unmountedRef.current) return;
      const { data, error } = await supabase.functions.invoke('transcribe-audio', {
        body: { audio_base64, audio_mime_type: mimeType },
      });
      if (error) {
        let status: number | undefined;
        let message: string | undefined;
        const ctx = (error as { context?: Response }).context;
        if (ctx) {
          status = ctx.status;
          try { const j = await ctx.json(); message = typeof j?.error === 'string' ? j.error : undefined; } catch { /* not JSON */ }
        }
        throw new Error(describeVoiceError(status, status === 502 || status === 400 ? message : undefined));
      }
      if (unmountedRef.current) return;
      if (typeof data?.text === 'string' && data.text.trim()) cbRef.current.onText(data.text);
      if (typeof data?.warning === 'string') cbRef.current.onWarning?.(data.warning);
      dispatch('done');
    } catch (e) {
      if (unmountedRef.current) return;
      dispatch('fail');
      cbRef.current.onError?.(e instanceof Error ? e.message : describeVoiceError(undefined, undefined));
    }
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') rec.stop();
  }, []);

  const start = useCallback(async () => {
    if (startingRef.current) return;
    const mimeType = pickMimeType((t) => MediaRecorder.isTypeSupported(t));
    if (!mimeType) return;
    startingRef.current = true;
    setStarting(true);
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        if (!unmountedRef.current) {
          cbRef.current.onError?.('Microphone access was blocked. Allow the microphone for this site in your browser settings, then try again.');
        }
        return;
      }
      if (unmountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      chunksRef.current = [];
      const fail = (message: string) => {
        // Detach first: a failed recorder still fires a final dataavailable + stop, which must not upload or toast again.
        const failed = recorderRef.current;
        if (failed) {
          failed.onstop = null;
          failed.onerror = null;
          failed.ondataavailable = null;
          try { if (failed.state !== 'inactive') failed.stop(); } catch { /* already stopped */ }
        }
        cleanup();
        chunksRef.current = [];
        dispatch('fail');
        cbRef.current.onError?.(message);
      };
      try {
        const rec = new MediaRecorder(stream, { mimeType });
        recorderRef.current = rec;
        rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
        rec.onerror = () => {
          if (unmountedRef.current) return;
          fail('Recording failed. Please try again.');
        };
        rec.onstop = () => {
          if (unmountedRef.current) return;
          const chunks = chunksRef.current;
          const hasAudio = hasRecordedAudio(chunks.map((c) => c.size));
          chunksRef.current = [];
          cleanup();
          if (!hasAudio) {
            dispatch('fail');
            cbRef.current.onError?.('No speech detected. Try again.');
            return;
          }
          void transcribe(new Blob(chunks, { type: mimeType }), mimeType);
        };
        dispatch('start');
        setElapsedMs(0);
        const startedAt = Date.now();
        timerRef.current = window.setInterval(() => {
          const ms = Date.now() - startedAt;
          setElapsedMs(ms);
          if (ms >= MAX_RECORDING_MS) { dispatch('stop'); stop(); }
        }, 250);
        rec.start();
      } catch {
        fail('Could not start recording. Please try again.');
      }
    } finally {
      startingRef.current = false;
      if (!unmountedRef.current) setStarting(false);
    }
  }, [cleanup, stop, transcribe]);

  const toggle = useCallback(() => {
    if (state === 'idle') { if (!startingRef.current) void start(); }
    else if (state === 'recording') { dispatch('stop'); stop(); }
  }, [state, start, stop]);

  return { available, state, elapsedMs, toggle, busy: starting };
}
